import {Component, OnInit} from '@angular/core';
import {EChartsOption} from "echarts";
import {
    EntityPerformanceChartComponent,
    PerformanceRecord
} from "../entity-performance-chart/entity-performance-chart.component";
import {
    DataType,
    GetMeterTreeData,
    MeterTreeMeasurementsResult,
    TimeRange,
    TimeResolution
} from "@flowmaps/flowmaps-typescriptmodels";
import {
    DataQueryFilters,
    MeasurementsHandlerOpenModalCommand
} from "../../measurements-component/measurements-handler.component";
import {Entity, EntityType} from "../../../handlers/entity";
import {ChartDataPerMeasurement} from "../../../utils/chart-data-provider";
import {Observable, of} from "rxjs";
import {Measurements} from "../../measurements-component/measurements";
import {lodash} from "../../../common/utils";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {sendQuery} from "../../../common/app-common-utils";
import {SourcesProvider} from "../../../utils/source-providers/sources-provider";
import {AppContext} from "../../../app-context";
import {OrganisationProvider} from "../../../utils/source-providers/organisation-provider";
import {ChartOptionType} from "../../dashboard/dashboard.types";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {MiddleEllipsisPipe} from "../../../common/middle-ellipsis.pipe";

@Component({
    selector: 'app-meter-tree-view',
    templateUrl: './meter-tree-view.component.html',
    styleUrl: './meter-tree-view.component.css'
})
export class MeterTreeViewComponent extends EntityPerformanceChartComponent implements OnInit {
    chartInstances = new Map<string, any>();
    chartOptions = new Map<string, EChartsOption>();
    treeData = new Map<string, any>();
    selectedDataType: DataType = DataType.electricityIntermediateConsumption;
    perSquareMeterEnabled = true;
    sourceProvider: SourcesProvider<any> = new OrganisationProvider();
    meters: Entity[]
    selectedTreeKey: string | null = null;
    selectedLocation: Entity | null = null;
    tagFilters: Record<string, string[]> = {};
    selectedTagFilters: Record<string, string | null> = {};

    ngOnInit() {
        super.ngOnInit();
        this.options.selectedDataType = this.selectedDataType;
        this.sendQuery("getEntityConsumptionType").subscribe(e => {
            if (e) this.options.selectedDataType = this.selectedDataType = e;
        });
        this.subscribeTo("getChartDataQueryFilters").subscribe((f: DataQueryFilters) => {
            if (f && f.sources?.some(s => s.type === 'location') && f.timeRange) {
                this.filters = f;
                this.selectedLocation = f.sources.find(s => s.type === 'location')?.source || null;
                this.refreshMeters();
                this.extractTagFilters();
                this.refresh();
            }
        });
    }

    private refreshMeters() {
        const allEntities = this.selectedLocation.location.connections.flatMap(c => [
            new Entity(this.selectedLocation.organisation, this.selectedLocation.location, c, null),
            ...c.meters.map(m => new Entity(this.selectedLocation.organisation, this.selectedLocation.location, c, m))
        ]);
        this.meters = this.applyTagFilters(this.filterMetersBasedOnSelectedDataType(allEntities));
    }

    private extractTagFilters() {
        this.tagFilters = {};
        this.selectedTagFilters = {};
        this.meters.forEach(m => {
            m.meter?.details?.tags?.forEach(t => {
                const category = t.category === 'custom' ? t.customType : t.category;
                this.tagFilters[category] ??= [];
                if (!this.tagFilters[category].includes(t.value)) {
                    this.tagFilters[category].push(t.value);
                }
            });
        });
    }

    private applyTagFilters(meters: Entity[]): Entity[] {
        return meters.filter(e => Object.entries(this.selectedTagFilters).every(([category, value]) =>
            (e.meter?.details?.tags || []).some(tag => (tag.customType ?? tag.category) === category && tag.value === value)));
    }

    refresh = () => {
        const timeRange = this.filters.timeRange;
        this.refreshMeters();
        this.getData(timeRange).subscribe(r => {
            const chartData: ChartDataPerMeasurement = {
                totals: r.byConnection,
                byEntity: r.byMeter.filter(v => this.meters.some(m => m.meter?.meterId === v.entityId))
            };
            this.setData(chartData);
            this.showChart = !!chartData.byEntity?.length;
        });
    };

    override setData(result: ChartDataPerMeasurement) {
        super.setData(result);
        this.updateMeterTree();
    }

    createData = (result: ChartDataPerMeasurement): Observable<PerformanceRecord[]> => {
        if (!result) return of([]);
        const meterRecords = (result.byEntity ?? []).map(m => {
            const entity = this.meters.find(e => m.entityId === e.getEntityId());
            if (!entity || !entity.meter) return null;
            const measurements = new Measurements(m, this.selectedDataType, this.filters.timeRange);
            const area = RefdataUtils.getMeterArea(entity.meter);
            const totalValue = lodash.sum(measurements.groupMeasurements());
            const value = this.showPerSquareMeter() ? (area ? lodash.round(totalValue / area, 1) : null) : totalValue;
            return <PerformanceRecord>{
                entityId: entity.meter.meterId,
                value,
                estimatedValue: null,
                label: RefdataUtils.meterFormatter(entity),
                parentId: entity.meter.parentId || entity.connection.connectionId,
                childIds: entity.getChildIds(),
                virtual: entity.meter.info.virtual,
                type: entity.meter.info.type || entity.meter.details?.type
            };
        }).filter((r): r is PerformanceRecord => !!r);
        const connectionRecords = (result.totals ?? []).map(r => {
            const connection = this.selectedLocation.location.connections.find(c => r.entityId === c.connectionId);
            if (!connection) return null;
            const measurements = new Measurements(r, this.intermediateMeasurementLink().get(this.getSelectedDataType), this.filters.timeRange);
            const totalValue = lodash.sum(measurements.groupMeasurements());
            return <PerformanceRecord>{
                entityId: connection.connectionId,
                value: totalValue,
                estimatedValue: null,
                label: connection.info?.code || 'Connection',
                parentId: null,
                childIds: connection.meters?.map(m => m.meterId) || [],
                type: null
            };
        }).filter((r): r is PerformanceRecord => !!r);
        return of([...meterRecords, ...connectionRecords]);
    };

    updateMeterTree() {
        const data = this.prepareTreeData();
        this.treeData.clear();
        this.chartOptions.clear();
        for (const tree of data) {
            this.treeData.set(tree.name, tree);
            this.chartOptions.set(tree.name, this.createOptionForTree(tree));
        }
        this.selectedTreeKey = data[0]?.name || null;
    }

    private findNearestAncestor(startParentId: string | undefined, connId: string,
                                included: Set<string>, meterData: Map<string, PerformanceRecord>): string | null {
        let p = startParentId;
        while (p) {
            if (p === connId) return null;
            if (included.has(p)) return p;
            const parentRec = meterData.get(p);
            if (!parentRec) break;
            p = parentRec.parentId;
        }
        return null;
    }

    private buildNode(rec: PerformanceRecord, rootValue: number, included: Set<string>,
                      meterData: Map<string, PerformanceRecord>, CONNECTION_LINE_WIDTH: number, MIN_WIDTH: number): any {
        const widthRatio = rootValue > 0 ? (rec.value || 0) / rootValue : 1;
        const width = Math.max(MIN_WIDTH, widthRatio * CONNECTION_LINE_WIDTH);
        const children = (rec.childIds || []).filter(id => included.has(id))
            .map(id => meterData.get(id)).filter(Boolean)
            .map(child => this.buildNode(child!, rootValue, included, meterData, CONNECTION_LINE_WIDTH, MIN_WIDTH));
        return {
            name: rec.label,
            value: rec.value,
            children,
            lineStyle: {width}
        };
    }

    prepareTreeData() {
        if (!this.records?.length || !this.meters?.length) return [];
        const connectionData = new Map<string, PerformanceRecord>();
        const meterData = new Map<string, PerformanceRecord>();
        this.records.forEach(r =>
            (r.type == null ? connectionData : meterData).set(r.entityId, r)
        );
        const CONNECTION_LINE_WIDTH = 30;
        const MIN_WIDTH = 1;
        const connectionIds = Array.from(
            new Set(this.meters.map(m => m.connection?.connectionId).filter(Boolean))
        );
        return connectionIds.map(connId => {
            const connRecord = connectionData.get(connId);
            const meterRecords = this.meters
                .filter(e => e.connection?.connectionId === connId)
                .map(e => meterData.get(e.meter?.meterId!))
                .filter((r): r is PerformanceRecord => !!r);
            const included = new Set(meterRecords.map(r => r.entityId));
            const topMeters = meterRecords.filter(r => r.parentId === connId);
            let total: number;
            if (connRecord?.value != null) {
                total = connRecord.value;
            } else if (topMeters.length > 0) {
                total = topMeters.reduce((sum, r) => sum + (r.value || 0), 0);
            } else {
                const firstLevelMeters = meterRecords.filter(r =>
                    this.findNearestAncestor(r.parentId, connId, included, meterData) === null
                );
                total = firstLevelMeters.reduce((sum, r) => sum + (r.value || 0), 0);
            }
            const childrenRoots = topMeters.length > 0 ? topMeters
                : meterRecords.filter(r => this.findNearestAncestor(r.parentId, connId, included, meterData) === null);
            const children = childrenRoots.map(mr => this.buildNode(mr, total, included, meterData, CONNECTION_LINE_WIDTH, MIN_WIDTH)).filter(Boolean);
            const name = connRecord?.label || this.meters.find(m => m.connection?.connectionId === connId)?.connection?.info?.code || connId;
            return {
                name,
                value: total,
                children
            };
        });
    }

    getData(timeRange: TimeRange): Observable<MeterTreeMeasurementsResult> {
        const locationIds = this.filters.cleanedSources.filter(s => s.type.includes("location"))
            .flatMap(s => [s.id, ...(s.aliases ?? [])]);
        return sendQuery("com.flowmaps.api.measurements.GetMeterTreeData", <GetMeterTreeData>{
            resolution: TimeResolution.month,
            timeRange: AppContext.timeRangeToQuery(timeRange),
            locationIds: locationIds,
            unrounded: true,
        });
    }

    filterMetersBasedOnSelectedDataType = (selection: Entity[]) => {
        switch (this.getSelectedDataType) {
            case DataType.electricityIntermediateConsumption:
                return selection.filter(e => (!e.meter || e.meter.info.type === "INTERMEDIATE") && e.connection?.info.connectionType === "Electricity");
            case DataType.waterIntermediateConsumption:
                return selection.filter(e => (!e.meter || e.meter.info.type === "INTERMEDIATE") && e.connection?.info.connectionType === "Water");
            case DataType.chargePointIntermediateConsumption:
                return selection.filter(e => (!e.meter || e.meter.info.type === "INTERMEDIATE") && e.connection?.info.connectionType === "ChargePoint");
            default:
                return selection;
        }
    }

    closeModal = () => this.sendCommandAndForget("closeModal");

    openModal = () => {
        this.sendCommandAndForget("openChartInModal", <MeasurementsHandlerOpenModalCommand>{
            component: MeterTreeViewComponent,
            chartOptions: {
                [ChartOptionType.Performance]: this.options
            },
            chartModalData: this.modalOptions,
            container: this.container
        });
    }

    get getSelectedDataType() {
        const selectedDataType = super.getSelectedDataType;
        return selectedDataType && this.possibleDataTypes().includes(selectedDataType) ? selectedDataType : this.selectedDataType;
    }

    intermediateMeasurementLink = (): Map<DataType, DataType> => {
        return new Map<DataType, DataType>()
            .set(DataType.electricityIntermediateConsumption, DataType.electricityConsumption)
            .set(DataType.chargePointIntermediateConsumption, DataType.chargePointConsumption)
            .set(DataType.waterIntermediateConsumption, DataType.waterConsumption);
    };

    entityType = (): EntityType => EntityType.meter

    possibleDataTypes = (): DataType[] => [DataType.electricityIntermediateConsumption,
        DataType.waterIntermediateConsumption, DataType.chargePointIntermediateConsumption]

    createOptionForTree(tree: any): EChartsOption {
        const percentOfRoot = (value: number) => {
            return tree.value ? (value / tree.value) * 100 : 100;
        };
        return {
            tooltip: {
                trigger: 'item',
                triggerOn: 'mousemove',
                borderRadius: 8,
                padding: 12,
                backgroundColor: '#fff',
                borderColor: '#dee2e6',
                borderWidth: 1,
                textStyle: {color: '#212529', fontSize: 14},
                formatter: (params: any) => {
                    const v = params.data;
                    const percent = tree.value ? (v.value / tree.value * 100).toFixed(1) : '100';
                    const dataType = params.dataIndex == 1 ? this.intermediateMeasurementLink().get(this.getSelectedDataType) : this.getSelectedDataType;
                    const dataTypeName = this.measurementName(dataType, true);
                    const color = DashboardContext.getMeasurementColor('current', dataType, 0);
                    return `
                    <div class="popover-content">
                        <div class="popover-header text-center text-nowrap fw-bold notranslate">${v.name}</div>
                        <div class="popover-body d-flex align-items-center justify-content-center gap-2 text-nowrap notranslate">
                            <span class="rounded-circle d-inline-block" style="width:0.5rem; height:0.5rem; background-color:${color};"></span>
                            <span class="text-muted">${dataTypeName}: ${this.chartUtils.defaultTooltipFormatter(v.value, dataType)} (${percent}%)</span>
                        </div>
                    </div>`;
                }
            },
            series: [{
                type: 'tree',
                data: [tree],
                top: '5%',
                bottom: '5%',
                symbol: 'roundRect',
                symbolSize: [200, 50],
                initialTreeDepth: 1,
                expandAndCollapse: true,
                layout: 'orthogonal',
                orient: 'LR',
                lineStyle: {curveness: 0.5},
                itemStyle: {
                    color: 'white',
                    borderWidth: 1.5,
                    borderRadius: 8,
                    shadowBlur: 8,
                    shadowColor: 'rgba(0,0,0,0.1)'
                } as any,
                label: {
                    position: 'inside',
                    verticalAlign: 'middle',
                    align: 'center',
                    distance: 10,
                    formatter: (params: any) => {
                        const v = params.data;
                        const percent = percentOfRoot(v.value);
                        return `{name|${MiddleEllipsisPipe.format(v.name, 14)}}\n{percent|${percent.toFixed(1)}%}`;
                    },
                    rich: {
                        name: {fontSize: 14, fontWeight: 500, color: '#222', lineHeight: 18, padding: [6, 8, 2, 8]},
                        percent: {
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: DashboardContext.getMeasurementColor('current', this.getSelectedDataType, 0),
                            borderRadius: 8,
                            padding: [2, 6],
                            verticalAlign: 'middle'
                        }
                    }
                },
                leaves: {
                    emphasis: {
                        focus: 'ancestor', scale: true
                    }
                },
                emphasis: {focus: 'descendant', scale: true}
            }]
        };
    }


    onChartInit(ec: any, key: string) {
        this.chartInstances.set(key, ec);
        ec.on('mousemove', {seriesIndex: 0}, (params: any) => {
            const node = params.data;
            if (!node) return;
            if (!node.children || node.children.length === 0) {
                ec.getZr().setCursorStyle('default');
            } else {
                ec.getZr().setCursorStyle('pointer');
            }
        });
    }

    onTagFilterChanged(category: string, selectedValue: string | null) {
        if (selectedValue === null) {
            delete this.selectedTagFilters[category];
        } else {
            this.selectedTagFilters[category] = selectedValue;
        }
        this.refresh();
    }

    handleChartClick(payload: { event: any, key: string | null }) {
        const {event, key} = payload;
        if (!key) return;
        this.onChartEvent(event, key);
    }

    onChartEvent($event: any, key: string) {
        const clickedName = $event.data?.name;
        if (!clickedName) return;
        const originalTree = this.treeData.get(key);
        if (!originalTree) return;
        const clickedNode = this.findNode([originalTree], clickedName);
        if (!clickedNode) return;
        const parentNode = $event.treeAncestors?.length
            ? this.findNode([originalTree], $event.treeAncestors[$event.treeAncestors.length - 2].name)
            : null;
        const siblings = parentNode?.children || [clickedNode];
        if (clickedNode.children?.length) {
            siblings.forEach(s => {
                if (s !== clickedNode) this.collapseNode(s);
            });
        }
        clickedNode.collapsed = clickedNode.collapsed === undefined ? false : !clickedNode.collapsed;
        this.treeData.set(key, originalTree);
        const chart = this.chartInstances.get(key);
        if (chart) {
            chart.setOption({
                series: [{data: [originalTree]}]
            });
        } else {
            const newOption = this.createOptionForTree(originalTree);
            this.chartOptions.set(key, newOption);
        }
    }

    collapseNode(node: any) {
        node.collapsed = true;
        if (!node.children || node.children.length === 0) return;
        node.children.forEach(child => {
            this.collapseNode(child);
        });
    }

    findNode(nodes: any[], name: string): any {
        for (const node of nodes) {
            if (node.name === name) return node;
            if (node.children?.length) {
                const found = this.findNode(node.children, name);
                if (found) return found;
            }
        }
        return null;
    }

    get treeOptions() {
        return Array.from(this.treeData.keys());
    }

    onTreeChanged(newKey: string) {
        this.selectedTreeKey = newKey;
    }
}
